import React from "react";

import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { briefingStalled } from "./briefingAnswers";
import BriefingProgress from "./BriefingProgress";
import { FLOW } from "./flowCopy";
import type { PlanFile } from "../../Types/AccountPlan";

interface Props {
  /** The version just confirmed, as the (polled) overview has it now. */
  file: PlanFile | null;
  open: boolean;
  onClose: () => void;
  /** Go to Review briefing; offered once the draft is ready. */
  onReview: () => void;
}

const BTN =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

/**
 * After "Confirm and generate briefing" the AE stays on the page and watches the
 * draft being written here (Sushant, 2026-11-03). Close keeps the run going in the
 * background; once the draft is ready the pop-up offers Review briefing.
 */
const BriefingRunDialog: React.FC<Props> = ({
  file,
  open,
  onClose,
  onReview,
}) => {
  const ready = file?.sponsor_view_status === "ready";
  const stalled = !ready && briefingStalled(file);
  const vN = file ? `v${file.plan_version ?? file.version}` : "";
  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/30" />
        <Dialog.Content
          data-testid="briefing-run-dialog"
          data-state={ready ? "ready" : stalled ? "stalled" : "running"}
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-32px)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-xl focus:outline-none"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <Dialog.Title className="text-lg font-bold text-gray-900">
              {ready
                ? `Draft briefing ready for ${vN}`
                : stalled
                  ? `The draft briefing for ${vN} is taking longer than usual`
                  : `Writing the draft briefing${vN ? ` for ${vN}` : ""}`}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
            >
              <X className="h-4 w-4" aria-hidden />
            </Dialog.Close>
          </div>
          {!ready && (
            <p className="text-xs text-gray-500">
              {stalled
                ? "Izba has been told. You'll be notified when it's ready to review."
                : "Usually under a minute. You can close this; it keeps going and you'll be notified when it's ready."}
            </p>
          )}
          {file && <BriefingProgress file={file} variant="done" />}
          <div className="mt-4 flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`${BTN} border border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}
            >
              Close
            </button>
            {ready && (
              <button
                type="button"
                data-testid="briefing-run-review"
                onClick={onReview}
                className={`${BTN} bg-[#974ae4] text-white hover:bg-[#8538d4]`}
              >
                {FLOW.reviewBriefing}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default BriefingRunDialog;
